import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Plus, Edit, Eye, Trash2, Upload, FileText, Download, Search, Filter } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { BUSINESS_SECTORS } from "@/utils/businessSectors";

interface Listing {
  id: string;
  title: string;
  teaser: string | null;
  description: string | null;
  sector: string;
  price: number | null;
  revenue: number | null;
  ebitda: number | null;
  location_suburb: string | null;
  location_state: string | null;
  location_country: string | null;
  images: string[] | null;
  documents: string[] | null;
  broker_id: string;
  status: 'draft' | 'live' | 'paused' | 'sold';
  featured: boolean | null;
  views_count: number | null;
  created_at: string;
  updated_at: string;
}

interface NewListing {
  title: string;
  teaser: string;
  description: string;
  sector: string;
  price: string;
  revenue: string;
  ebitda: string;
  location_suburb: string;
  location_state: string;
  status: 'draft' | 'live' | 'paused' | 'sold';
}

// Use imported BUSINESS_SECTORS

const states = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

export function BrokerListings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [listings, setListings] = useState<Listing[]>([]);
  const [userRole, setUserRole] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sectorFilter, setSectorFilter] = useState<string>("all");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingListing, setEditingListing] = useState<Listing | null>(null);
  const [formData, setFormData] = useState<NewListing>({
    title: '',
    teaser: '',
    description: '',
    sector: '',
    price: '',
    revenue: '',
    ebitda: '',
    location_suburb: '',
    location_state: '',
    status: 'draft'
  });

  useEffect(() => {
    if (user) {
      fetchListings();
      fetchUserRole();
    }
  }, [user]);

  const fetchUserRole = async () => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user?.id);

      if (error) throw error;
      // Get the first role (prioritize broker if multiple roles exist)
      const roles = data || [];
      const brokerRole = roles.find(r => r.role === 'broker');
      setUserRole(brokerRole?.role || roles[0]?.role || '');
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchListings = async () => {
    try {
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('broker_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setListings((data as unknown as Listing[]) || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast({
        title: "Error",
        description: "Failed to fetch listings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createListing = async () => {
    try {
      const listingData = {
        ...formData,
        price: formData.price ? parseInt(formData.price) : null,
        revenue: formData.revenue ? parseInt(formData.revenue) : null,
        ebitda: formData.ebitda ? parseInt(formData.ebitda) : null,
        broker_id: user?.id,
        location_country: 'Australia'
      };

      const { error } = await supabase
        .from('listings')
        .insert([listingData]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Listing created successfully",
      });

      setShowCreateForm(false);
      resetForm();
      fetchListings();
    } catch (error) {
      console.error('Error creating listing:', error);
      toast({
        title: "Error",
        description: "Failed to create listing",
        variant: "destructive",
      });
    }
  };

  const updateListing = async () => {
    if (!editingListing) return;

    try {
      const listingData = {
        ...formData,
        price: formData.price ? parseInt(formData.price) : null,
        revenue: formData.revenue ? parseInt(formData.revenue) : null,
        ebitda: formData.ebitda ? parseInt(formData.ebitda) : null,
      };

      const { error } = await supabase
        .from('listings')
        .update(listingData)
        .eq('id', editingListing.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Listing updated successfully",
      });

      setEditingListing(null);
      resetForm();
      fetchListings();
    } catch (error) {
      console.error('Error updating listing:', error);
      toast({
        title: "Error",
        description: "Failed to update listing",
        variant: "destructive",
      });
    }
  };

  const deleteListing = async (listingId: string) => {
    if (!confirm('Are you sure you want to delete this listing?')) return;

    try {
      console.log('Attempting to delete listing:', listingId);
      console.log('Current user ID:', user?.id);
      
      const { error } = await supabase
        .from('listings')
        .delete()
        .eq('id', listingId)
        .eq('broker_id', user?.id);

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      toast({
        title: "Success",
        description: "Listing deleted successfully",
      });

      // Force refresh the listings
      await fetchListings();
    } catch (error) {
      console.error('Error deleting listing:', error);
      toast({
        title: "Error",
        description: `Failed to delete listing: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const payAndPublish = async (listingId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-listing-payment', {
        body: { listing_id: listingId },
      });
      if (error) throw error;
      if (data?.url) {
        const newTab = window.open('', '_blank');
        if (newTab) {
          newTab.location.href = data.url;
        } else {
          window.location.href = data.url;
        }
        toast({ title: 'Payment Required', description: 'Complete payment to publish your listing.' });
      } else {
        toast({ title: 'Payment Error', description: 'Could not start checkout. Please try again.', variant: 'destructive' });
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      teaser: '',
      description: '',
      sector: '',
      price: '',
      revenue: '',
      ebitda: '',
      location_suburb: '',
      location_state: '',
      status: 'draft'
    });
  };

  const startEdit = (listing: Listing) => {
    setFormData({
      title: listing.title,
      teaser: listing.teaser || '',
      description: listing.description || '',
      sector: listing.sector,
      price: listing.price?.toString() || '',
      revenue: listing.revenue?.toString() || '',
      ebitda: listing.ebitda?.toString() || '',
      location_suburb: listing.location_suburb || '',
      location_state: listing.location_state || '',
      status: listing.status
    });
    setEditingListing(listing);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'sold': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredListings = listings.filter(listing => {
    const matchesSearch = 
      listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.sector.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.location_suburb?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || listing.status === statusFilter;
    const matchesSector = sectorFilter === 'all' || listing.sector === sectorFilter;
    
    return matchesSearch && matchesStatus && matchesSector;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Listings Management</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="animate-pulse">
                <div className="h-4 bg-muted rounded w-1/2"></div>
                <div className="h-8 bg-muted rounded w-1/4"></div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const totalListings = listings.length;
  const liveListings = listings.filter(l => l.status === 'live').length;
  const draftListings = listings.filter(l => l.status === 'draft').length;
  const totalViews = listings.reduce((sum, l) => sum + (l.views_count || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Listings Management</h1>
        <Sheet open={showCreateForm} onOpenChange={setShowCreateForm}>
          <SheetTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Listing
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Create New Listing</SheetTitle>
              <SheetDescription>
                Add a new business listing to your portfolio
              </SheetDescription>
            </SheetHeader>
            <ListingForm 
              formData={formData}
              setFormData={setFormData}
              onSubmit={createListing}
              onCancel={() => setShowCreateForm(false)}
              isEditing={false}
            />
          </SheetContent>
        </Sheet>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Listings</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalListings}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Live Listings</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{liveListings}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Draft Listings</CardTitle>
            <Edit className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{draftListings}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalViews}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Listings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by title, sector, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Sector" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {BUSINESS_SECTORS.map(sector => (
                  <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Listings Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Listings ({filteredListings.length})</CardTitle>
          <CardDescription>
            Manage your business listings and track their performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Sector</TableHead>
                <TableHead>Price Range</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Views</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredListings.map((listing) => (
                <TableRow key={listing.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{listing.title}</div>
                      {listing.teaser && (
                        <div className="text-sm text-muted-foreground line-clamp-1">
                          {listing.teaser}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{listing.sector}</TableCell>
                  <TableCell>
                    {listing.price ? `$${listing.price.toLocaleString()}` : 'POA'}
                  </TableCell>
                  <TableCell>
                    {listing.location_suburb && listing.location_state ? (
                      `${listing.location_suburb}, ${listing.location_state}`
                    ) : 'Not specified'}
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(listing.status)}>
                      {listing.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{listing.views_count || 0}</TableCell>
                  <TableCell>
                    {format(new Date(listing.created_at), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                     <div className="flex items-center gap-2">
                       {(userRole === 'broker' || userRole === 'seller') && (
                         <Button
                           variant="ghost"
                           size="sm"
                           aria-label="Edit listing"
                           onClick={() => startEdit(listing)}
                         >
                           <Edit className="h-4 w-4 mr-1" />
                           Edit
                         </Button>
                       )}
                       <Button
                         variant="ghost"
                         size="sm"
                         onClick={() => window.open(`/business/${listing.id}`, '_blank')}
                       >
                         <Eye className="h-4 w-4" />
                       </Button>
                       {listing.status === 'draft' && (
                         <Button
                           size="sm"
                           onClick={() => payAndPublish(listing.id)}
                         >
                           Pay & Publish
                         </Button>
                       )}
                       {(userRole === 'broker' || userRole === 'seller') && (
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => deleteListing(listing.id)}
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       )}
                     </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredListings.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No listings found matching your criteria
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      {editingListing && (
        <Dialog open={!!editingListing} onOpenChange={() => setEditingListing(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Listing</DialogTitle>
              <DialogDescription>
                Update your business listing details
              </DialogDescription>
            </DialogHeader>
            <ListingForm 
              formData={formData}
              setFormData={setFormData}
              onSubmit={updateListing}
              onCancel={() => setEditingListing(null)}
              isEditing={true}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

interface ListingFormProps {
  formData: NewListing;
  setFormData: (data: NewListing) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isEditing: boolean;
}

function ListingForm({ formData, setFormData, onSubmit, onCancel, isEditing }: ListingFormProps) {
  return (
    <div className="space-y-4 mt-6">
      <div>
        <Label htmlFor="title">Business Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Enter business title"
          required
        />
      </div>

      <div>
        <Label htmlFor="sector">Sector *</Label>
        <Select value={formData.sector} onValueChange={(value) => setFormData({ ...formData, sector: value })}>
          <SelectTrigger>
            <SelectValue placeholder="Select sector" />
          </SelectTrigger>
          <SelectContent>
            {BUSINESS_SECTORS.map(sector => (
              <SelectItem key={sector} value={sector}>{sector}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="teaser">Teaser</Label>
        <Input
          id="teaser"
          value={formData.teaser}
          onChange={(e) => setFormData({ ...formData, teaser: e.target.value })}
          placeholder="Brief description that appears in listings"
        />
      </div>

      <div>
        <Label htmlFor="description">Full Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Detailed business description"
          rows={4}
        />
      </div>

      <div>
        <Label htmlFor="price">Price</Label>
        <Input
          id="price"
          type="number"
          value={formData.price}
          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
          placeholder="Business price"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="revenue">Annual Revenue</Label>
          <Input
            id="revenue"
            type="number"
            value={formData.revenue}
            onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
            placeholder="Annual revenue"
          />
        </div>
        <div>
          <Label htmlFor="ebitda">EBITDA</Label>
          <Input
            id="ebitda"
            type="number"
            value={formData.ebitda}
            onChange={(e) => setFormData({ ...formData, ebitda: e.target.value })}
            placeholder="EBITDA"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="location_suburb">Suburb</Label>
          <Input
            id="location_suburb"
            value={formData.location_suburb}
            onChange={(e) => setFormData({ ...formData, location_suburb: e.target.value })}
            placeholder="Suburb"
          />
        </div>
        <div>
          <Label htmlFor="location_state">State</Label>
          <Select value={formData.location_state} onValueChange={(value) => setFormData({ ...formData, location_state: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {states.map(state => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select value={formData.status} onValueChange={(value: any) => setFormData({ ...formData, status: value })}>
          <SelectTrigger>
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="live">Live</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2 pt-4">
        <Button onClick={onSubmit} className="flex-1">
          {isEditing ? 'Update Listing' : 'Create Listing'}
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}